import React, { useCallback, useState } from 'react'
import moment from 'moment'
import { SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'components/Luxkit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'components/Luxkit/Modal/Modal'
import LineRefundIcon from 'components/Luxkit/Icons/line/LineRefundIcon'
import TextLink from 'components/Luxkit/TextLink'
import { INTL_DATE_TIME_FORMAT, INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import styled from 'styled-components'
import { Trans, useTranslation } from 'react-i18next'

const MiddledTooltipIcon = styled(SolidInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  propertyTimezoneOffset?: number;
  checkInDate?: string | moment.Moment;
  isFlashUSP?: boolean;
}

function OfferPropertyCancellationPolicies(props: Props) {
  const { propertyTimezoneOffset, checkInDate, isFlashUSP } = props
  const [modalOpen, setModalOpen] = useState(false)

  const { t } = useTranslation('hotelOffer')

  const startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  const startBasedOnCheckIn = checkInDate && (propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days'))
  let startDate
  if (startBasedOnCheckIn) {
    startDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
  }

  const onItemClick = useCallback(() => {
    setModalOpen(true)
  }, [])

  const onClose = useCallback(() => {
    setModalOpen(false)
  }, [])

  if (startDate?.isBefore(moment())) {
    return null
  }

  return <>
    {!isFlashUSP && <Clickable onClick={onItemClick}>
      {!startDate && <Trans i18nKey="nonFlashFreeCancellationLabel" ns="hotelOffer">
        <BodyText variant="medium" weight="semi-bold" as="span" colour="highlight">Free cancellation</BodyText>{' '}
        <BodyText variant="medium" as="span">within 7 days of purchase (hotel only)</BodyText>
      </Trans>}
      {startDate && <Trans i18nKey="nonFlashFreeCancellationLabelWithDate" ns="hotelOffer">
        <BodyText variant="medium" weight="bold" as="span" colour="highlight">Free cancellation</BodyText>{' '}
        <BodyText variant="medium" as="span">until {{ date: startDate, ...INTL_DAY_MONTH_NAME_SHORT } as any} (hotel only)</BodyText>
      </Trans>}
      {' '}<MiddledTooltipIcon size="XS" />
    </Clickable>}
    {isFlashUSP && <TextLink
      size="medium"
      variant="highlight"
      underline={false}
      data-type="7day"
      onClick={onItemClick}
      startIcon={<LineRefundIcon />}
    >
      {t('Free cancellation', { ns: 'hotelOffer' })}
    </TextLink>
}
    <Modal
      title={t('Free cancellation', { ns: 'hotelOffer' })}
      isOpen={modalOpen}
      onClose={onClose}
    >
      {!checkInDate && <BodyText variant="medium" as="p">
        {t('flashChangeOfMindGuaranteeExplanation', { ns: 'hotelOffer' })}
      </BodyText>}
      {checkInDate && <BodyText variant="medium" as="p">
        {t('flashChangeOfMindGuaranteeDate', { ns: 'hotelOffer', date: startDate, ...INTL_DATE_TIME_FORMAT })}
      </BodyText>}
      <HelpPane />
    </Modal>
  </>
}

export default OfferPropertyCancellationPolicies
